import React from "react";
import { useTranslation } from "react-i18next";

const ROUTE_URL = "https://maps.app.goo.gl/xsVNfuTcjFbTxpFS9";
const MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2033.8983804711252!2d13.584893816301923!3d52.682977852244385!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a9b3ef32881057%3A0xaaa4953f3df28761!2sS-Arena%20Bernau!5e0!3m2!1sde!2sde!4v1730122476432!5m2!1sde!2sde";

function parseLabeledText(value) {
  if (typeof value !== "string" || !value.includes(":")) {
    return { title: null, body: value };
  }
  const idx = value.indexOf(":");
  const title = value.slice(0, idx).trim();
  const body = value.slice(idx + 1).trim();
  if (!title || !body) return { title: null, body: value };
  return { title, body };
}

function EventLocation() {
  const { t } = useTranslation();

  const addressRaw = t("eventLocation.address");
  const transportRaw = t("eventLocation.publicTransport");
  const addressParsed = parseLabeledText(addressRaw);
  const transportParsed = parseLabeledText(transportRaw);

  const infoRows = [
    {
      id: "address",
      icon: "fas fa-map-marker-alt",
      label: addressParsed.title ?? t("eventLocation.addressLabel"),
      text: addressParsed.body ?? addressRaw,
    },
    {
      id: "transport",
      icon: "fas fa-bus",
      label: transportParsed.title ?? t("eventLocation.publicTransportLabel"),
      text: transportParsed.body ?? transportRaw,
    },
    {
      id: "parking",
      icon: "fas fa-parking",
      label: t("eventLocation.parkingLabel"),
      text: t("eventLocation.parking"),
    },
    {
      id: "capacity",
      icon: "fas fa-users",
      label: t("eventLocation.capacityLabel"),
      text: t("eventLocation.capacity"),
    },
    {
      id: "accessibility",
      icon: "fas fa-wheelchair",
      label: t("eventLocation.accessibilityLabel"),
      text: t("eventLocation.accessibility"),
    },
  ];

  return (
    <section
      className="relative z-10 bg-white pt-12 pb-12 transition-colors dark:bg-slate-800"
      id="location"
      aria-labelledby="event-location-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="event-location-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("eventLocation.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("eventLocation.description")}
          </p>
        </header>

        <figure className="group/card mb-10 overflow-hidden rounded-2xl border border-slate-200 shadow-sm dark:border-slate-700">
          <div className="relative h-64 overflow-hidden sm:h-72 md:h-80 lg:h-96">
            <img
              src={`${process.env.PUBLIC_URL}/images/arena.webp`}
              alt={t("eventLocation.imageAlt")}
              width={1920}
              height={1080}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover/card:scale-[1.02]"
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/35 to-transparent"
              aria-hidden
            />
            <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <h3 className="flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
                <img
                  src={`${process.env.PUBLIC_URL}/images/sponsors/sparkasse.png`}
                  alt={t("eventLocation.sponsorLogoAlt")}
                  width={160}
                  height={40}
                  loading="lazy"
                  decoding="async"
                  className="h-8 w-auto max-w-[140px] object-contain object-left drop-shadow-md md:h-10"
                />
                <span className="min-w-0">{t("eventLocation.arenaName")}</span>
              </h3>
            </figcaption>
          </div>
        </figure>

        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
          <article className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 md:p-8">
            <ul className="m-0 list-none divide-y divide-slate-200 p-0 dark:divide-slate-700">
              {infoRows.map((row) => (
                <li key={row.id} className="flex gap-4 py-5 first:pt-0">
                  <span
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light"
                    aria-hidden
                  >
                    <i className={`${row.icon} text-lg`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {row.label}
                    </h4>
                    <p className="text-base leading-relaxed text-slate-900 dark:text-slate-100">
                      {row.text}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 border-t border-slate-200 pt-6 dark:border-slate-700">
              <a
                href={ROUTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                title={t("contact.open_in_maps")}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:bg-primary-light dark:text-white dark:hover:bg-primary dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-800"
              >
                <i className="fas fa-directions text-sm" aria-hidden />
                {t("eventLocation.planRoute")}
              </a>
            </div>
          </article>

          <div className="flex min-h-[280px] h-[min(55vw,380px)] w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-700 dark:bg-slate-900/40 sm:h-[min(50vw,420px)] md:h-[min(45vw,480px)] md:min-h-[400px] lg:h-full lg:min-h-0">
            <div className="relative min-h-[280px] w-full flex-1 md:min-h-[400px]">
              <iframe
                src={MAP_EMBED_SRC}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t("eventLocation.mapTitle")}
                className="absolute inset-0 h-full w-full border-0"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EventLocation;
